import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../store';
import { analyzeReport, prepareStateForNewAnalysis, setAnalyzeReportStatusLoadingState, setExecutionId } from '../redux/suggestions';
import { FetchLoadingStateEnum } from '../constants';
import { AnalysisMessageData, MessageReceived, MessageReceivedEvent } from '../models/webSockets';
import { useNavigate } from 'react-router';
import { WordApiService } from '../services/word/api';
import { selectActiveJurisdiction } from '../selectors/suggestions';
import useFeatureFlags from './useFeatureFlags';

export type StartAnalyzeReportParams = {
  itpExecutionId: string;
  jurisdictionId?: string;
  useMockResponse: boolean;
};

export const useAnalyzeReport = () => {

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { getFlag } = useFeatureFlags();
  const activeJurisdiction = useSelector(selectActiveJurisdiction);

  const startAnalyzeReport = async ({ itpExecutionId, jurisdictionId, useMockResponse }: StartAnalyzeReportParams) => {
    try {
      dispatch(prepareStateForNewAnalysis());
      const projectJurisdictionSelectorCP1 = getFlag('projectJurisdictionSelectorCp1');
      const url = Office.context.document.url;
      const filename = url.split("/").pop();
      const contentType = "application/json";
      const wordApi = new WordApiService();
      const groupedData = await wordApi.withContext(async () => {
        const structuredBody = await wordApi.getWordFileAsStructuredJsonWithHeadingTextPairs();
        return wordApi.groupByHeading(structuredBody);
      });

      const payload = projectJurisdictionSelectorCP1 ? {
        documentSections: groupedData,
        jurisdiction: jurisdictionId ?? activeJurisdiction,
      } : groupedData;

      const data: BodyInit = JSON.stringify(payload);
      await dispatch(analyzeReport({ data, itpExecutionId, contentType, filename, useMockResponse }));
    } catch (error) {
      console.error('Error initiating analyze report:', error); // TODO: Implement error handling
    }
  };

  const analysisMessageHandler = async (message: MessageReceived<AnalysisMessageData>) => {
    try {
      switch (message.event) {
        case MessageReceivedEvent.ANALYSIS_START:
          dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.loading));
          dispatch(setExecutionId(message.data.itpExecutionId));
          navigate('/analysis');
          break;
        case MessageReceivedEvent.ANALYSIS_END:
          dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.fulfilled));
          break;
        case MessageReceivedEvent.ANALYSIS_ERROR:
          dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.error));
          break;
        default:
          break;
      }
    } catch (e) {
      console.error('Failed to parse WebSocket message:', e);
      dispatch(setAnalyzeReportStatusLoadingState(FetchLoadingStateEnum.error));
    }
  };

  return {
    startAnalyzeReport,
    analysisMessageHandler,
  };
};

